import { useState, useMemo, useEffect } from "react"
import { useGlobalSettings } from "@/entities/globalSettings/useGlobalSettings"
import { YoungsFilterSearchBar } from "../filters/YoungsFilterSearchBar"
import { useYoungs } from "@/entities/user/useYoung"
import { QueryRender } from "../helpers/QueryRender"
import { User } from "@/entities/user/user"
import { UserSmallProfile } from "../ui/UserSmallProfile"

export type SelectionType =
   | {
        type: "group"
        name: string
        youngs: User[]
     }
   | {
        type: "young"
        young: User
     }

interface Props {
   onSelect: (selection: SelectionType) => void
   onClose: () => void
}

export function YoungOrGroupSelectorDialog({ onSelect, onClose }: Props) {
   const [searchText, setSearchText] = useState("")
   const [selectedGroups, setSelectedGroups] = useState<string[]>([])
   const { data: youngs = [], ...query } = useYoungs()
   const globalSettingsQuery = useGlobalSettings()

   useEffect(() => {
      if (globalSettingsQuery.data) {
         setSelectedGroups(globalSettingsQuery.data.groups)
      }
   }, [globalSettingsQuery.data, globalSettingsQuery.data?.groups])

   // TODO : move this to a hook and use elsewhere (or encapsate it in FaireAppelFilterSearchBar)
   const filteredYoungs = useMemo(
      () =>
         youngs
            .filter(
               (young) =>
                  selectedGroups.includes(young.group) &&
                  (!searchText ||
                     young.firstName.toLowerCase().startsWith(searchText.toLowerCase()) ||
                     young.lastName.toLowerCase().startsWith(searchText.toLowerCase())),
            )
            .sort((a, b) => a.firstName.localeCompare(b.firstName)),
      [youngs, selectedGroups, searchText],
   )

   function handleSelect(
      selection: { type: "group"; value: string } | { type: "young"; value: User },
   ) {
      if (selection.type === "group") {
         onSelect({
            type: "group",
            name: selection.value === "all" ? "Tous les groupes" : selection.value,
            youngs: youngs
               .filter((young) => selection.value === "all" || young.group === selection.value)
               .sort((a, b) => a.firstName.localeCompare(b.firstName)),
         })
      } else {
         onSelect({
            type: "young",
            young: selection.value,
         })
      }
   }

   return (
      <div className="modal modal-open">
         <div className="modal-box max-w-3xl flex flex-col h-full">
            <button onClick={onClose} className="btn btn-sm btn-circle absolute right-2 top-2 z-99">
               ✕
            </button>

            <h3 className="font-bold text-lg md:mb-6">Choisissez un groupe ou un jeune</h3>

               <div className="divider divider-neutral md:mb-8">Groupes</div>
               <div className="flex flex-wrap gap-2">
                  <button
                     key="all"
                     className="btn btn-primary"
                     onClick={() => handleSelect({ type: "group", value: "all" })}
                  >
                     Tous les groupes
                  </button>
                  <QueryRender query={globalSettingsQuery}>
                     {(settings) =>
                        settings.groups.map((group) => (
                           <button
                              key={group}
                              className="btn btn-primary"
                              onClick={() => handleSelect({ type: "group", value: group })}
                           >
                              {group}
                           </button>
                        ))
                     }
                  </QueryRender>
               </div>
               <div className="md:mb-8">
               </div>


               <div className="divider divider-neutral md:mb-8">Jeunes</div>

               <YoungsFilterSearchBar
                  searchText={searchText}
                  setSearchText={setSearchText}
                  selectedGroups={selectedGroups}
                  setSelectedGroups={setSelectedGroups}
               />

               <QueryRender query={query} data={youngs}>
                  {() => (
                     <div className="flex-1 min-h-0 overflow-y-auto mt-4 space-y-2 px-4  ">
                        {filteredYoungs.map((young) => (
                           <button
                              key={young.id}
                              className="w-full flex items-center gap-2 p-3 hover:bg-base-200 rounded-lg  "
                              onClick={() => handleSelect({ type: "young", value: young })}
                           >
                              <UserSmallProfile user={young} />
                           </button>
                        ))}
                        </div>
                  )}
               </QueryRender>
 
         </div>
      </div>
   )
}
